import { Trans } from 'next-i18next';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { insertAdvertInArray } from '@adverts/business/adverts.utils';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { useAuthModal } from '@auth/business/AuthModal.hook';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { ContentCardLoader } from '@components/content/ContentCard/ContentCardLoader.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { MAX_PREFERENCES_SERVICES } from '@settings/business/settings.utils';
import { RootState } from '@store/store';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { buildContentCard, buildContentFavorisServiceCard } from '@utils/card';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';
import { ServicesHomeContext } from 'pages/les-outils';
import styles from './ServicesHomeLayout.module.scss';

type Props = ComponentPropsWithRef<'main'> & {};

function getNeedAuthentication(openSigninModal: () => void) {
  const SignInLink = () => (
    <BasicLink isUnderlined={true} onClick={openSigninModal}>
      connecter
    </BasicLink>
  );
  const SubscribeLink = () => (
    <BasicLink href={urlEnum.signUp} isUnderlined={true}>
      vous inscrire
    </BasicLink>
  );
  return (
    <Trans i18nKey="services.home.need-authentication" ns="services">
      Pour ajouter <SignInLink /> ou <SubscribeLink />
    </Trans>
  );
}

export const ServicesHomeLayout: FC<Props> = ({ className, ...rest }) => {
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const {
    services: { data: services, isValidating: servicesLoading },
    pagination: { data: pagination },
    fetchNextServices,
  } = useContext(ServicesHomeContext);

  const favoriteServices = useSelector((state: RootState) => state.services.services);
  const favoriteServicesLoading = useSelector((state: RootState) => state.loading.effects.services.fetchUserServices);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const servicesList = useMemo(
    () =>
      services
        ? insertAdvertInArray(
            services.map((item) => buildContentCard(item, isSmartphone)),
            3,
            3000,
            ADVERT_FORMAT_ID.NativeAdBis,
            { className: styles.advertCard }
          )
        : undefined,
    [services, favoriteServices]
  );
  const favoriteServicesList = useMemo(() => favoriteServices.map(buildContentFavorisServiceCard), [favoriteServices]);
  const isMobile = useIsBreakpointDown(Breakpoint.m);

  const rootClass = classnames(`${styles['services-home-layout']} plc-page`, className);
  const adClassName = useClassNames({ all: 'plc-mt-m', m: ['plc-mt-0', 'plc-ml-m'] });

  const { showSignInModal } = useAuthModal();

  const BREADCRUMB_STEPS: BreadCrumbStep[] = [
    {
      label: 'common.newspaper',
      linkProps: { as: urlEnum.home, href: urlEnum.home },
    },
    {
      label: 'common.services.menu-title',
      linkProps: { as: urlEnum.services, href: urlEnum.services },
    },
  ];

  function onSeeMoreClick() {
    if (fetchNextServices) {
      fetchNextServices();
    }
  }

  useAdvert({
    pageId: ADVERT_PAGE_ID.ServicesHome,
  });

  const tagProps: PlcTagProps = useMemo(
    () => ({
      level2: ATLevel2.Services,
      tagPageNameI18nArray: ['services.tag.title'],
    }),
    []
  );

  return (
    <PlcPageLayout
      className={rootClass}
      metaDescI18n="services.home.meta-desc"
      metaTitleI18n="services.home.meta-title"
      tagProps={tagProps}
      {...rest}
    >
      <section className="wrapper-s">
        <div className={isSmartphone ? '' : styles['grid-parent']}>
          <PlcPageHeader
            breadcrumbSteps={BREADCRUMB_STEPS}
            className={`plc-mb-xl no-padding ${styles['grid-header']}`}
            i18nTitle="services.home.title"
            withBlockAd={false}
          >
            <Text i18nKey="services.home.description" variant="p" />
          </PlcPageHeader>
          <div className={styles['grid-favoris']}>
            <Text
              className="plc-mb-basis"
              i18nKey="services.home.favorites-title"
              tag="h2"
              variant={isMobile ? 'h4' : 'h3'}
            />
            <div className="plc-mb-m">
              {(!isAuthenticated || favoriteServices.length === 0) && (
                <Text i18nKey="services.home.no-favorites" tag="p" variant="small" />
              )}
              {!isAuthenticated && (
                <Text tag="p" variant="small">
                  {getNeedAuthentication(showSignInModal)}
                </Text>
              )}
              {isAuthenticated && (
                <>
                  {favoriteServices.length > 0 && favoriteServices.length < MAX_PREFERENCES_SERVICES && (
                    <Text
                      i18nKey="services.home.favorites-number"
                      i18nOptions={{
                        count: favoriteServices.length,
                      }}
                      tag="div"
                      variant="small"
                    />
                  )}
                  {favoriteServices.length === MAX_PREFERENCES_SERVICES && (
                    <Text tag="div" variant="small">
                      <Trans i18nKey="services.home.max-favorites" ns="services">
                        Vous avez atteint <br /> Vous devez en supprimer
                      </Trans>
                    </Text>
                  )}
                  {favoriteServices.length === 0 && (
                    <Text i18nKey="services.home.how-to-add" tag="div" variant="small" />
                  )}
                </>
              )}
            </div>
            <div className="grid-m-3-s-2">
              {favoriteServicesLoading ? (
                <>
                  <ContentCardLoader uniqueKey="favoriteServices1" />
                  <ContentCardLoader uniqueKey="favoriteServices2" />
                  <ContentCardLoader uniqueKey="favoriteServices3" />
                </>
              ) : (
                favoriteServicesList
              )}
            </div>
          </div>

          <div
            className={
              isSmartphone ? 'plc-mb-basis plc-pt-basis' : `${styles['grid-pub']} ${styles['mobile-pub-center']}`
            }
          >
            <GenericAd className={adClassName} formatId={ADVERT_FORMAT_ID.WideAngle} />
          </div>
        </div>
      </section>
      <section className="wrapper-s">
        <div className={`grid-m-3-s-2 ${pagination ? 'plc-mb-xl' : ''}`}>
          {servicesList}
          {servicesLoading && (
            <>
              <ContentCardLoader uniqueKey="services1" />
              <ContentCardLoader uniqueKey="services2" />
              <ContentCardLoader uniqueKey="services3" />
            </>
          )}
        </div>
        {pagination && !servicesLoading && (
          <div className={styles['services-home-layout-pagination']}>
            <Text
              className="plc-mb-m"
              i18nKey="search.results.pagination"
              i18nOptions={{ current: pagination.current, total: pagination.count }}
              variant="p"
            />
            {pagination.hasNext && (
              <SecondaryButton onClick={onSeeMoreClick} textProps={{ i18nKey: 'search.results.see-more' }} />
            )}
          </div>
        )}
      </section>
    </PlcPageLayout>
  );
};
